import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  B2BBillingPeriodChargeLineInput,
  B2BBillingPreview,
  B2BBillingPreviewCategory,
  B2BBillingPreviewContract,
  B2BInvoice,
  B2BTelemetrySuggestion,
  B2bInvoiceService
} from '../../../services/b2b-invoice.service';
import { ContractsService } from '../../../services/contracts.service';
import { B2bAccessory } from '../../../models/Contract';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { saveAs } from 'file-saver';

type WizardStep = 'hub' | 'mirror' | 'type' | 'launch' | 'preview' | 'drafts';

interface LaunchLine {
  b2BContractItemId?: number | null;
  b2BAccessoryId?: number | null;
  vehicleId?: number | null;
  plate?: string | null;
  itemNumber?: string | null;
  vehicleUnitValue?: number | null;
  description?: string | null;
  quantity: number;
  unitAmount: number;
  selected?: boolean;
}

@Component({
  selector: 'app-b2b-billing-closing',
  templateUrl: './b2b-billing-closing.component.html',
  styleUrls: ['../../b2b/shared/b2b-operational-shared.css', './b2b-billing-closing.component.css']
})
export class B2bBillingClosingComponent implements OnInit {
  step: WizardStep = 'hub';
  filterYear: number | null = null;
  filterMonth: number | null = null;
  periodStart = '';
  periodEnd = '';
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  companyName = '';
  customerName = '';

  loading = false;
  saving = false;
  launching = false;
  preview: B2BBillingPreview | null = null;
  selectedCategory = '';
  selectedContract: B2BBillingPreviewContract | null = null;
  selectedCategoryPreview: B2BBillingPreviewCategory | null = null;
  drafts: B2BInvoice[] = [];
  selectedDraft: B2BInvoice | null = null;
  lineSearch = '';
  lastDraftMessage = '';
  readonly previewPageSize = 50;

  launchLines: LaunchLine[] = [];
  launchSearch = '';
  /** Valor em lote (ex.: telemetria R$ 50) aplicado nas linhas filtradas/selecionadas. */
  bulkUnitAmount: number | null = 50;
  bulkQuantity: number | null = null;
  accessoriesCatalog: B2bAccessory[] = [];
  /** Tipo/qtd/valor acima da lista de placas (acessórios). */
  bulkAccessoryId: number | null = null;
  bulkAccessoryQty = 1;
  bulkAccessoryUnit = 0;
  newAccessoryId: number | null = null;
  newAccessoryPlate = '';
  newAccessoryQty = 1;
  newAccessoryUnit = 0;

  readonly categories = [
    { value: 'Rental', label: 'Aluguel' },
    { value: 'Reserve', label: 'Moto reserva' },
    { value: 'TrafficFine', label: 'Multas' },
    { value: 'Damage', label: 'Avarias' },
    { value: 'Deposit', label: 'Caução' },
    { value: 'Claim', label: 'Sinistro' },
    { value: 'Telemetry', label: 'Telemetria' },
    { value: 'Accessory', label: 'Acessórios' }
  ];

  constructor(
    private invoiceService: B2bInvoiceService,
    private contractsService: ContractsService,
    private route: ActivatedRoute,
    private router: Router,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    this.filterYear = prev.getFullYear();
    this.filterMonth = prev.getMonth() + 1;
    this.periodStart = this.toInputDate(prev);
    this.periodEnd = this.toInputDate(prevEnd);

    this.route.queryParamMap.subscribe(q => {
      const cid = q.get('contractId');
      this.contractId = cid ? Number(cid) : null;
      this.loadContractInfo();
      this.loadDrafts();
      if (q.get('wizard') === '1' || this.contractId) {
        this.openWizard();
      } else {
        this.step = 'hub';
        this.loadPreview();
      }
    });
  }

  get needsLaunchStep(): boolean {
    return this.selectedCategory === 'Telemetry'
      || this.selectedCategory === 'Accessory'
      || this.selectedCategory === 'Reserve'
      || this.selectedCategory === 'Rental';
  }

  /** Categorias do espelho do contrato no período (breakdown). */
  get mirrorCategories(): Array<{
    category: string;
    categoryLabel: string;
    lineCount: number;
    totalAmount: number;
    sampleLines: Array<{ plate?: string | null; description?: string | null; amount: number }>;
  }> {
    if (!this.preview?.contracts?.length) return [];
    const contracts = this.contractId
      ? this.preview.contracts.filter(c => c.b2BContractId === this.contractId)
      : this.preview.contracts;

    const map = new Map<string, {
      category: string;
      categoryLabel: string;
      lineCount: number;
      totalAmount: number;
      sampleLines: Array<{ plate?: string | null; description?: string | null; amount: number }>;
    }>();

    for (const c of contracts) {
      for (const cat of c.categories || []) {
        const key = cat.category;
        const existing = map.get(key) || {
          category: cat.category,
          categoryLabel: cat.categoryLabel || cat.category,
          lineCount: 0,
          totalAmount: 0,
          sampleLines: []
        };
        existing.lineCount += Number(cat.lineCount) || (cat.lines?.length || 0);
        existing.totalAmount += Number(cat.totalAmount) || 0;
        for (const line of cat.lines || []) {
          if (existing.sampleLines.length >= 5) break;
          existing.sampleLines.push({
            plate: line.plate,
            description: line.description,
            amount: Number(line.amount) || 0
          });
        }
        map.set(key, existing);
      }
    }

    return Array.from(map.values()).sort((a, b) => a.categoryLabel.localeCompare(b.categoryLabel, 'pt-BR'));
  }

  /** Nome da empresa / contrato para exibição (nunca só o id). */
  get contractDisplayName(): string {
    return (
      this.companyName
      || this.customerName
      || this.contractName
      || this.contractNumber
      || (this.contractId ? `Contrato ${this.contractId}` : '')
    );
  }

  get wizardTitle(): string {
    const base = 'Assistente de fechamento';
    if (this.contractDisplayName) return `${base} — ${this.contractDisplayName}`;
    return base;
  }

  loadContractInfo(): void {
    if (!this.contractId) {
      this.contractNumber = '';
      this.contractName = '';
      this.companyName = '';
      this.customerName = '';
      return;
    }
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c: any) => {
        this.contractNumber = c?.contractNumber || c?.ContractNumber || '';
        this.contractName = c?.contractName || c?.ContractName || '';
        this.companyName = c?.companyName || c?.CompanyName || '';
        this.customerName = c?.customerName || c?.CustomerName || '';
      },
      error: () => {
        // Mantém fallback pelo id se a API falhar
      }
    });
  }

  get stepIndex(): number {
    switch (this.step) {
      case 'mirror': return 0;
      case 'type': return 1;
      case 'launch': return 2;
      case 'preview': return this.needsLaunchStep ? 3 : 2;
      case 'drafts': return this.needsLaunchStep ? 4 : 3;
      default: return -1;
    }
  }

  get body() {
    this.syncCompetenceFromPeriod();
    return {
      competenceYear: this.filterYear || undefined,
      competenceMonth: this.filterMonth || undefined,
      periodStart: this.periodStart || undefined,
      periodEnd: this.periodEnd || undefined,
      b2BContractId: this.contractId || undefined,
      category: this.selectedCategory || undefined
    };
  }

  get categoryLabel(): string {
    return this.categories.find(c => c.value === this.selectedCategory)?.label || this.selectedCategory;
  }

  get previewLines(): Array<B2BBillingPreviewCategory['lines'][number] & { contractNumber?: string }> {
    if (!this.preview || !this.selectedCategory) return [];
    const rows: Array<B2BBillingPreviewCategory['lines'][number] & { contractNumber?: string }> = [];
    for (const c of this.preview.contracts) {
      const cat = c.categories.find(x => x.category === this.selectedCategory);
      if (!cat) continue;
      for (const line of cat.lines) {
        rows.push({ ...line, contractNumber: c.contractNumber });
      }
    }
    return rows;
  }

  get filteredPreviewLines() {
    const q = (this.lineSearch || '').trim().toLowerCase();
    if (!q) return this.previewLines;
    return this.previewLines.filter(l =>
      (l.plate || '').toLowerCase().includes(q)
      || (l.description || '').toLowerCase().includes(q)
      || (l.contractNumber || '').toLowerCase().includes(q)
    );
  }

  get previewTotal(): number {
    return this.previewLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  }

  get launchTotal(): number {
    return this.launchLines
      .filter(l => l.selected !== false)
      .reduce((s, l) => s + this.lineAmount(l), 0);
  }

  /** Telemetria: mensalidade × qtd (mês comercial ≥ 30 dias no período). */
  get telemetryPeriodDays(): number {
    const start = this.parseInputDate(this.periodStart);
    const end = this.parseInputDate(this.periodEnd);
    if (!start || !end) return 0;
    return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 86400000) + 1);
  }

  get selectedLaunchCount(): number {
    return this.launchLines.filter(l => l.selected !== false && this.lineAmount(l) > 0).length;
  }

  get filteredLaunchLines(): LaunchLine[] {
    const q = (this.launchSearch || '').trim().toLowerCase();
    if (!q) return this.launchLines;
    return this.launchLines.filter(l =>
      (l.plate || '').toLowerCase().includes(q)
      || (l.itemNumber || '').toLowerCase().includes(q)
      || (l.description || '').toLowerCase().includes(q)
      || String(l.b2BContractItemId || '').includes(q)
    );
  }

  get allFilteredLaunchSelected(): boolean {
    const rows = this.filteredLaunchLines;
    return rows.length > 0 && rows.every(l => l.selected !== false);
  }

  set allFilteredLaunchSelected(value: boolean) {
    this.toggleSelectAllLaunch(!!value);
  }

  toggleSelectAllLaunch(checked: boolean): void {
    for (const line of this.filteredLaunchLines) {
      line.selected = checked;
    }
  }

  /** Aplica valor unitário (e opcionalmente qtd) nas linhas filtradas selecionadas. */
  applyBulkUnitToSelected(): void {
    const unit = Number(this.bulkUnitAmount);
    if (!(unit > 0)) {
      this.errorService.showError('Informe um valor válido para aplicar.');
      return;
    }
    const qty = this.bulkQuantity != null && Number(this.bulkQuantity) > 0
      ? Number(this.bulkQuantity)
      : null;
    let n = 0;
    for (const line of this.filteredLaunchLines) {
      if (line.selected === false) continue;
      line.unitAmount = unit;
      if (qty != null) line.quantity = qty;
      n++;
    }
    if (!n) {
      this.errorService.showError('Nenhuma linha selecionada para aplicar o valor.');
      return;
    }
    this.errorService.showSuccessMessage(`Valor R$ ${unit.toFixed(2)} aplicado em ${n} linha(s).`);
  }

  onBulkAccessoryChange(): void {
    const acc = this.accessoriesCatalog.find(a => a.id === this.bulkAccessoryId);
    this.bulkAccessoryUnit = Number(acc?.unitValue) || 0;
  }

  /** Aplica tipo/qtd/valor de acessório nas placas selecionadas (filtradas). */
  applyBulkAccessoryToSelected(): void {
    if (!this.bulkAccessoryId) {
      this.errorService.showError('Selecione o tipo de acessório.');
      return;
    }
    const acc = this.accessoriesCatalog.find(a => a.id === this.bulkAccessoryId);
    if (!acc) return;
    const qty = Number(this.bulkAccessoryQty) || 1;
    const unit = Number(this.bulkAccessoryUnit) || Number(acc.unitValue) || 0;
    if (!(qty > 0) || !(unit > 0)) {
      this.errorService.showError('Informe quantidade e valor válidos.');
      return;
    }
    let n = 0;
    for (const line of this.filteredLaunchLines) {
      if (line.selected === false) continue;
      line.b2BAccessoryId = acc.id;
      line.quantity = qty;
      line.unitAmount = unit;
      const plate = (line.plate || '').trim().toUpperCase() || 'sem placa';
      line.description = `Acessório — ${acc.name} — ${plate}`;
      n++;
    }
    if (!n) {
      this.errorService.showError('Selecione ao menos uma placa.');
      return;
    }
    this.errorService.showSuccessMessage(`Acessório aplicado em ${n} placa(s).`);
  }

  lineAmount(line: LaunchLine): number {
    const qty = Number(line.quantity) || 0;
    const unit = Number(line.unitAmount) || 0;
    if (qty <= 0 || unit <= 0) return 0;
    // Telemetria: mês comercial 30 dias — nunca diária × 31.
    if (this.selectedCategory === 'Telemetry') {
      const days = this.telemetryPeriodDays;
      if (days >= 30) return Math.round(qty * unit * 100) / 100;
      const daily = Math.round((unit / 30) * 10000) / 10000;
      return Math.round(daily * days * qty * 100) / 100;
    }
    // Reserva/Aluguel: quantity = dias; unit = mensalidade → mensal × (dias÷30).
    if (this.selectedCategory === 'Reserve' || this.selectedCategory === 'Rental') {
      const days = Math.min(Math.round(qty), 30);
      if (days <= 0) return 0;
      if (days >= 30) return Math.round(unit * 100) / 100;
      const daily = Math.round((unit / 30) * 10000) / 10000;
      return Math.round(daily * days * 100) / 100;
    }
    return Math.round(qty * unit * 100) / 100;
  }

  openWizard(): void {
    this.step = 'mirror';
    this.selectedCategory = '';
    this.launchLines = [];
    this.loadPreview();
  }

  closeWizard(): void {
    this.step = 'hub';
    this.loadDrafts();
    this.loadPreview();
  }

  backToContract(): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'faturas']);
  }

  loadPreview(): void {
    if (!this.validatePeriod(false)) return;
    this.loading = true;
    this.invoiceService.previewClosing({
      competenceYear: this.filterYear || undefined,
      competenceMonth: this.filterMonth || undefined,
      periodStart: this.periodStart || undefined,
      periodEnd: this.periodEnd || undefined,
      b2BContractId: this.contractId || undefined
    }).subscribe({
      next: (raw) => {
        this.preview = this.normalizePreview(raw);
        this.loading = false;
        if (this.selectedCategory) this.refreshCategoryPreview();
      },
      error: (err) => {
        this.loading = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  loadDrafts(): void {
    this.invoiceService.listDrafts(this.filterYear || undefined, this.filterMonth || undefined).subscribe({
      next: (list) => {
        let items = (list || []).map(x => this.normalizeInvoice(x));
        if (this.contractId) {
          items = items.filter(i => i.b2BContractId === this.contractId);
        }
        this.drafts = items;
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  applyFilters(): void {
    this.loadPreview();
    this.loadDrafts();
  }

  onPeriodChange(): void {
    this.syncCompetenceFromPeriod();
  }

  goStep(step: WizardStep): void {
    if (step === 'preview' && !this.selectedCategory) return;
    if (step === 'launch' && !this.needsLaunchStep) return;
    this.step = step;
  }

  chooseCategory(category: string): void {
    this.selectedCategory = category;
    if (this.needsLaunchStep) {
      if (!this.contractId) {
        this.errorService.showError('Selecione um contrato para lançar aluguel, telemetria, acessórios ou moto reserva.');
        return;
      }
      this.step = 'launch';
      this.prepareLaunchStep();
      return;
    }
    // Multas/avarias/etc.: recarrega a prévia com a categoria.
    this.step = 'preview';
    this.loadPreview();
  }

  prepareLaunchStep(): void {
    if (!this.contractId) return;
    // Garante que o painel de lançamento aparece (não fica preso em "Carregando espelho…").
    this.loading = false;
    this.launching = true;
    this.launchLines = [];
    this.launchSearch = '';
    if (this.selectedCategory === 'Telemetry') {
      this.bulkUnitAmount = 50;
      this.bulkQuantity = 1;
    } else if (this.selectedCategory === 'Reserve' || this.selectedCategory === 'Rental') {
      this.bulkUnitAmount = null;
      this.bulkQuantity = Math.min(Math.max(this.telemetryPeriodDays, 1), 30);
    } else {
      this.bulkUnitAmount = null;
      this.bulkQuantity = null;
    }

    if (this.selectedCategory === 'Telemetry') {
      this.invoiceService.listPeriodCharges(
        this.contractId, this.periodStart, this.periodEnd, 'Telemetry'
      ).subscribe({
        next: (existing) => {
          const saved = (existing || []).map(c => this.normalizeChargeLine(c));
          if (saved.length) {
            this.launchLines = saved;
            this.launching = false;
            return;
          }
          this.invoiceService.suggestTelemetry(this.contractId!).subscribe({
            next: (suggestions) => {
              this.launchLines = (suggestions || []).map(s => this.suggestionToLine(this.normalizeSuggestion(s), 'Telemetry'));
              this.launching = false;
            },
            error: (err) => this.failLaunch(err)
          });
        },
        error: (err) => this.failLaunch(err)
      });
      return;
    }

    if (this.selectedCategory === 'Reserve') {
      this.invoiceService.listPeriodCharges(
        this.contractId, this.periodStart, this.periodEnd, 'Reserve'
      ).subscribe({
        next: (existing) => {
          const saved = (existing || []).map(c => this.normalizeChargeLine(c));
          if (saved.length) {
            this.launchLines = saved;
            this.launching = false;
            return;
          }
          this.invoiceService.suggestReserve(this.contractId!, this.periodStart, this.periodEnd).subscribe({
            next: (suggestions) => {
              this.launchLines = (suggestions || []).map(s => this.suggestionToLine(this.normalizeSuggestion(s), 'Reserve'));
              this.launching = false;
            },
            error: (err) => this.failLaunch(err)
          });
        },
        error: (err) => this.failLaunch(err)
      });
      return;
    }

    if (this.selectedCategory === 'Rental') {
      this.invoiceService.listPeriodCharges(
        this.contractId, this.periodStart, this.periodEnd, 'Rental'
      ).subscribe({
        next: (existing) => {
          const saved = (existing || []).map(c => this.normalizeChargeLine(c));
          if (saved.length) {
            this.launchLines = saved;
            this.launching = false;
            return;
          }
          this.loadRentalSuggestions();
        },
        error: (err) => {
          // Tabela PeriodCharges ausente: mostra erro e ainda tenta listar veículos do contrato.
          this.showPeriodChargeMissingIfNeeded(err);
          this.loadRentalSuggestions();
        }
      });
      return;
    }

    // Accessory: espelho de todas as placas do contrato + tipo/qtd/valor em lote.
    this.contractsService.getB2bAccessories(this.contractId).subscribe({
      next: (list) => {
        this.accessoriesCatalog = (list || []).map(a => this.normalizeAccessory(a));
        this.invoiceService.listPeriodCharges(
          this.contractId!, this.periodStart, this.periodEnd, 'Accessory'
        ).subscribe({
          next: (existing) => {
            const saved = (existing || []).map(c => this.normalizeChargeLine(c));
            if (saved.length) {
              this.launchLines = saved.map(l => ({ ...l, selected: true }));
              this.launching = false;
              return;
            }
            this.loadAccessoryVehicleMirror();
          },
          error: (err) => {
            this.showPeriodChargeMissingIfNeeded(err);
            this.loadAccessoryVehicleMirror();
          }
        });
      },
      error: (err) => this.failLaunch(err)
    });
  }

  /** Lista todas as placas do contrato para lançar acessório por veículo. */
  private loadAccessoryVehicleMirror(): void {
    if (!this.contractId) {
      this.launching = false;
      return;
    }
    this.contractsService.listAllB2bContractVehicles(this.contractId, 50, 40).subscribe({
      next: (vehicles) => {
        this.launchLines = (vehicles || []).map((v: any) => {
          const plate = (v.plate || v.Plate || '').toString().trim().toUpperCase() || null;
          return {
            b2BContractItemId: v.b2BContractItemId ?? v.B2BContractItemId ?? null,
            vehicleId: v.vehicleId ?? v.VehicleId ?? null,
            plate,
            itemNumber: v.itemNumber ?? v.ItemNumber ?? null,
            b2BAccessoryId: null,
            description: plate ? `Acessório — ${plate}` : 'Acessório',
            quantity: 0,
            unitAmount: 0,
            selected: false
          } as LaunchLine;
        });
        this.launching = false;
        if (!this.launchLines.length) {
          this.errorService.showError('Nenhum veículo marcado neste contrato para acessórios.');
        }
      },
      error: (err) => this.failLaunch(err)
    });
  }

  private failLaunch(err: unknown): void {
    this.launching = false;
    this.loading = false;
    if (this.showPeriodChargeMissingIfNeeded(err)) return;
    this.errorService.handleHttpError(err);
  }

  /** Detecta migration pendente da B2BBillingPeriodCharges e avisa em tela. */
  private showPeriodChargeMissingIfNeeded(err: unknown): boolean {
    const msg = this.extractHttpErrorMessage(err);
    if (!/B2BBillingPeriodCharges|Invalid object name/i.test(msg)) return false;
    this.errorService.showError(
      'Tabela B2BBillingPeriodCharges ainda não existe no banco. Rode a migration do Common (pipeline Database) e tente de novo.'
    );
    return true;
  }

  private extractHttpErrorMessage(err: unknown): string {
    const e = err as { error?: { error?: string; message?: string } | string; message?: string };
    if (typeof e?.error === 'string') return e.error;
    if (e?.error && typeof e.error === 'object') {
      return String(e.error.error || e.error.message || '');
    }
    return String(e?.message || '');
  }

  addAccessoryLine(): void {
    if (!this.newAccessoryId) {
      this.errorService.showError('Selecione o tipo de acessório.');
      return;
    }
    const plate = (this.newAccessoryPlate || '').trim().toUpperCase();
    if (!plate) {
      this.errorService.showError('Informe a placa do veículo que recebeu o acessório.');
      return;
    }
    const acc = this.accessoriesCatalog.find(a => a.id === this.newAccessoryId);
    if (!acc) return;
    const unit = Number(this.newAccessoryUnit) || Number(acc.unitValue) || 0;
    const qty = Number(this.newAccessoryQty) || 1;
    this.launchLines = [
      ...this.launchLines,
      {
        b2BAccessoryId: acc.id,
        plate,
        description: `Acessório — ${acc.name} — ${plate}`,
        quantity: qty,
        unitAmount: unit,
        selected: true
      }
    ];
    this.newAccessoryId = null;
    this.newAccessoryPlate = '';
    this.newAccessoryQty = 1;
    this.newAccessoryUnit = 0;
  }

  onAccessorySelectChange(): void {
    const acc = this.accessoriesCatalog.find(a => a.id === this.newAccessoryId);
    this.newAccessoryUnit = Number(acc?.unitValue) || 0;
  }

  removeLaunchLine(index: number): void {
    this.launchLines = this.launchLines.filter((_, i) => i !== index);
  }

  saveLaunchAndContinue(): void {
    if (!this.contractId || !this.validatePeriod()) return;

    const lines: B2BBillingPeriodChargeLineInput[] = this.launchLines
      .filter(l => l.selected !== false)
      .map(l => ({
        b2BContractItemId: l.b2BContractItemId,
        b2BAccessoryId: l.b2BAccessoryId,
        vehicleId: l.vehicleId,
        plate: l.plate,
        description: l.description,
        quantity: Number(l.quantity) || 0,
        unitAmount: Number(l.unitAmount) || 0
      }))
      .filter(l => (l.quantity * l.unitAmount) > 0);

    if (this.selectedCategory === 'Accessory') {
      if (lines.some(l => !l.plate)) {
        this.errorService.showError('Informe a placa em todas as linhas de acessório.');
        return;
      }
      if (lines.some(l => !l.b2BAccessoryId)) {
        this.errorService.showError('Aplique o tipo de acessório nas placas selecionadas antes de salvar.');
        return;
      }
    }

    if (!lines.length) {
      this.errorService.showError('Selecione ao menos uma linha com quantidade e valor.');
      return;
    }

    this.saving = true;
    this.invoiceService.replacePeriodCharges(this.contractId, {
      periodStart: this.periodStart,
      periodEnd: this.periodEnd,
      category: this.selectedCategory,
      lines
    }).subscribe({
      next: () => {
        this.errorService.showSuccessMessage('Lançamentos do período salvos.');
        this.invoiceService.previewClosing(this.body).subscribe({
          next: (raw) => {
            this.preview = this.normalizePreview(raw);
            this.refreshCategoryPreview();
            this.saving = false;
            this.step = 'preview';
          },
          error: (err) => {
            this.saving = false;
            this.errorService.handleHttpError(err);
            this.step = 'preview';
          }
        });
      },
      error: (err) => {
        this.saving = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  refreshCategoryPreview(): void {
    if (!this.preview || !this.selectedCategory) {
      this.selectedCategoryPreview = null;
      this.selectedContract = null;
      return;
    }

    const contracts = this.preview.contracts
      .map(c => ({
        ...c,
        categories: c.categories.filter(cat => cat.category === this.selectedCategory)
      }))
      .filter(c => c.categories.length > 0);

    this.selectedContract = contracts[0] || null;
    this.selectedCategoryPreview = this.selectedContract?.categories[0] || null;
  }

  createDraft(): void {
    if (!this.selectedCategory) {
      this.errorService.showError('Escolha o tipo de fechamento.');
      return;
    }
    if (!this.validatePeriod()) return;

    const msg = this.previewLines.length
      ? `Cobranças de ${this.categoryLabel} de ${this.periodStart} a ${this.periodEnd} (R$ ${this.formatMoney(this.previewTotal)}). Gerar rascunho?`
      : `Não há linhas novas de ${this.categoryLabel} no período. Deseja regenerar o rascunho mesmo assim?`;

    this.confirmationService.confirm({
      title: `Rascunho — ${this.categoryLabel}`,
      message: msg,
      confirmText: 'Gerar rascunho'
    }).subscribe({
      next: (ok) => {
        if (!ok) return;
        this.saving = true;
        this.invoiceService.createDraft(this.body).subscribe({
          next: (result) => {
            this.saving = false;
            this.lastDraftMessage =
              `${result.invoicesCreated} criado(s), ${result.invoicesUpdated} atualizado(s).`;
            if ((result.invoicesCreated || 0) + (result.invoicesUpdated || 0) === 0) {
              this.errorService.showError(
                `Nenhum rascunho gerado. ${this.preview?.diagnosticMessage || 'Verifique o lançamento do período.'}`
              );
            } else {
              this.errorService.showSuccessMessage(`Rascunho salvo: ${this.lastDraftMessage}`);
            }
            this.loadDrafts();
            this.loadPreview();
            this.step = 'drafts';
            if (result.invoices?.length) {
              this.selectedDraft = this.normalizeInvoice(result.invoices[0]);
            }
          },
          error: (err) => {
            this.saving = false;
            this.errorService.handleHttpError(err);
          }
        });
      }
    });
  }

  selectDraft(invoice: B2BInvoice): void {
    this.invoiceService.getById(invoice.id).subscribe({
      next: (inv) => this.selectedDraft = this.normalizeInvoice(inv),
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  exportDraft(format: 'xlsx' | 'pdf'): void {
    if (!this.selectedDraft) {
      this.errorService.showError('Selecione um rascunho para exportar.');
      return;
    }
    this.invoiceService.exportInvoice(this.selectedDraft.id, format).subscribe({
      next: (blob) => {
        const ext = format === 'pdf' ? 'pdf' : 'csv';
        saveAs(blob, `${this.selectedDraft!.invoiceNumber}.${ext}`);
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  formatMoney(value: number | null | undefined): string {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatPeriodLabel(value: string | null | undefined): string {
    if (!value) return '—';
    const d = this.parseInputDate(value.includes('T') ? value.slice(0, 10) : value);
    if (!d) return value;
    return d.toLocaleDateString('pt-BR');
  }

  formatLineDate(value?: string | null): string {
    if (!value) return '—';
    return this.formatPeriodLabel(value);
  }

  private syncCompetenceFromPeriod(): void {
    if (!this.periodEnd) return;
    const end = this.parseInputDate(this.periodEnd);
    if (!end) return;
    this.filterYear = end.getFullYear();
    this.filterMonth = end.getMonth() + 1;
  }

  private validatePeriod(showError = true): boolean {
    if (!this.periodStart || !this.periodEnd) {
      if (showError) this.errorService.showError('Informe data de início e data de término do fechamento.');
      return false;
    }
    const start = this.parseInputDate(this.periodStart);
    const end = this.parseInputDate(this.periodEnd);
    if (!start || !end) {
      if (showError) this.errorService.showError('Datas inválidas.');
      return false;
    }
    if (end < start) {
      if (showError) this.errorService.showError('A data de término deve ser maior ou igual à de início.');
      return false;
    }
    return true;
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [y, m, d] = value.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
  }

  private normalizeSuggestion(raw: any): B2BTelemetrySuggestion {
    return {
      b2BContractItemId: raw.b2BContractItemId ?? raw.B2BContractItemId ?? null,
      itemNumber: raw.itemNumber ?? raw.ItemNumber ?? null,
      vehicleId: raw.vehicleId ?? raw.VehicleId ?? null,
      snapshotId: raw.snapshotId ?? raw.SnapshotId ?? null,
      plate: raw.plate ?? raw.Plate ?? null,
      trackerType: raw.trackerType ?? raw.TrackerType ?? null,
      trackerId: raw.trackerId ?? raw.TrackerId ?? null,
      vehicleUnitValue: Number(raw.vehicleUnitValue ?? raw.VehicleUnitValue ?? 0) || null,
      suggestedUnitAmount: Number(raw.suggestedUnitAmount ?? raw.SuggestedUnitAmount ?? 0) || 0,
      suggestedQuantity: Number(raw.suggestedQuantity ?? raw.SuggestedQuantity ?? 1) || 1
    };
  }

  private loadRentalSuggestions(): void {
    if (!this.contractId) {
      this.launching = false;
      this.loading = false;
      return;
    }
    this.invoiceService.suggestRental(this.contractId, this.periodStart, this.periodEnd).subscribe({
      next: (suggestions) => {
        const rows = (suggestions || []).map(s => this.suggestionToLine(this.normalizeSuggestion(s), 'Rental'));
        if (rows.length) {
          this.launchLines = rows;
          this.launching = false;
          this.loading = false;
          return;
        }
        this.loadRentalFromContractVehicles();
      },
      error: (err) => {
        this.showPeriodChargeMissingIfNeeded(err);
        this.loadRentalFromContractVehicles();
      }
    });
  }

  /** Fallback: lista placas marcadas no contrato (mesmo endpoint da tela de veículos). */
  private loadRentalFromContractVehicles(): void {
    if (!this.contractId) {
      this.launching = false;
      return;
    }
    const days = Math.min(Math.max(this.telemetryPeriodDays, 1), 30);
    this.contractsService.getB2bContractItems(this.contractId, 1, 5000).subscribe({
      next: (itemsRes: any) => {
        const items = itemsRes?.data || itemsRes?.Data || itemsRes?.items || itemsRes?.Items || [];
        const unitByItemId = new Map<number, number>();
        for (const it of items) {
          const id = Number(it.id ?? it.Id);
          const unit = Number(it.unitValue ?? it.UnitValue ?? 0) || 0;
          if (id) unitByItemId.set(id, unit);
        }
        this.contractsService.listAllB2bContractVehicles(this.contractId!, 50, 40).subscribe({
          next: (vehicles) => {
            this.launchLines = (vehicles || []).map((v: any) => {
              const plate = (v.plate || v.Plate || '').toString().trim().toUpperCase() || null;
              const itemId = Number(v.b2BContractItemId ?? v.B2BContractItemId ?? v.b2bContractItemId ?? 0) || null;
              const monthly = Number(
                v.unitValue ?? v.UnitValue
                ?? (itemId ? unitByItemId.get(itemId) : 0)
                ?? 0
              ) || 0;
              return {
                b2BContractItemId: itemId,
                vehicleId: v.vehicleId ?? v.VehicleId ?? null,
                plate,
                itemNumber: v.itemNumber ?? v.ItemNumber ?? null,
                vehicleUnitValue: monthly || null,
                description: `Aluguel — ${plate || 'sem placa'}`,
                quantity: days,
                unitAmount: monthly,
                selected: true
              } as LaunchLine;
            });
            this.launching = false;
            if (!this.launchLines.length) {
              this.errorService.showError('Nenhum veículo marcado neste contrato para listar no aluguel.');
            }
          },
          error: (err) => {
            this.launching = false;
            this.errorService.handleHttpError(err);
          }
        });
      },
      error: () => {
        this.contractsService.listAllB2bContractVehicles(this.contractId!, 50, 40).subscribe({
          next: (vehicles) => {
            this.launchLines = (vehicles || []).map((v: any) => {
              const plate = (v.plate || v.Plate || '').toString().trim().toUpperCase() || null;
              return {
                b2BContractItemId: v.b2BContractItemId ?? v.B2BContractItemId ?? null,
                vehicleId: v.vehicleId ?? v.VehicleId ?? null,
                plate,
                itemNumber: v.itemNumber ?? v.ItemNumber ?? null,
                description: `Aluguel — ${plate || 'sem placa'}`,
                quantity: days,
                unitAmount: Number(v.unitValue ?? v.UnitValue ?? 0) || 0,
                selected: true
              } as LaunchLine;
            });
            this.launching = false;
          },
          error: (err) => {
            this.launching = false;
            this.errorService.handleHttpError(err);
          }
        });
      }
    });
  }

  private suggestionToLine(s: B2BTelemetrySuggestion, category: 'Telemetry' | 'Reserve' | 'Rental' = 'Telemetry'): LaunchLine {
    const plate = s.plate || 'sem placa';
    const desc =
      category === 'Reserve' ? `Moto reserva — ${plate}`
      : category === 'Rental' ? `Aluguel — ${plate}`
      : `Telemetria — ${plate}`;
    return {
      b2BContractItemId: s.b2BContractItemId ?? null,
      vehicleId: s.vehicleId ?? null,
      plate: s.plate ?? null,
      itemNumber: s.itemNumber ?? null,
      vehicleUnitValue: Number(s.vehicleUnitValue || 0) || null,
      description: desc,
      quantity: Number(s.suggestedQuantity) || (category === 'Reserve' || category === 'Rental' ? 30 : 1),
      unitAmount: Number(s.suggestedUnitAmount) || Number(s.vehicleUnitValue) || 0,
      selected: true
    };
  }

  private normalizeChargeLine(raw: any): LaunchLine {
    return {
      b2BContractItemId: raw.b2BContractItemId ?? raw.B2BContractItemId,
      b2BAccessoryId: raw.b2BAccessoryId ?? raw.B2BAccessoryId,
      vehicleId: raw.vehicleId ?? raw.VehicleId,
      plate: raw.plate ?? raw.Plate,
      description: raw.description ?? raw.Description,
      quantity: Number(raw.quantity ?? raw.Quantity ?? 1) || 1,
      unitAmount: Number(raw.unitAmount ?? raw.UnitAmount ?? 0) || 0,
      selected: true
    };
  }

  private normalizeAccessory(raw: any): B2bAccessory {
    return {
      id: raw.id ?? raw.Id,
      b2bContractId: raw.b2bContractId ?? raw.b2BContractId ?? raw.B2BContractId,
      code: raw.code ?? raw.Code,
      name: raw.name ?? raw.Name,
      description: raw.description ?? raw.Description,
      unitValue: Number(raw.unitValue ?? raw.UnitValue ?? 0),
      isActive: !!(raw.isActive ?? raw.IsActive ?? true)
    };
  }

  private normalizePreview(raw: any): B2BBillingPreview {
    const mirror = raw.mirrorLast30Days ?? raw.MirrorLast30Days ?? {};
    return {
      competenceYear: raw.competenceYear ?? raw.CompetenceYear,
      competenceMonth: raw.competenceMonth ?? raw.CompetenceMonth,
      periodStart: raw.periodStart ?? raw.PeriodStart,
      periodEnd: raw.periodEnd ?? raw.PeriodEnd,
      competenceTotal: Number(raw.competenceTotal ?? raw.CompetenceTotal ?? 0),
      eligibleItemCount: Number(raw.eligibleItemCount ?? raw.EligibleItemCount ?? 0),
      eligibleVehicleCount: Number(raw.eligibleVehicleCount ?? raw.EligibleVehicleCount ?? 0),
      itemsActivatedForMonthlyClosing: Number(
        raw.itemsActivatedForMonthlyClosing ?? raw.ItemsActivatedForMonthlyClosing ?? 0
      ),
      diagnosticMessage: raw.diagnosticMessage ?? raw.DiagnosticMessage ?? null,
      mirrorLast30Days: {
        from: mirror.from ?? mirror.From,
        to: mirror.to ?? mirror.To,
        totalAmount: Number(mirror.totalAmount ?? mirror.TotalAmount ?? 0),
        lineCount: Number(mirror.lineCount ?? mirror.LineCount ?? 0),
        categories: (mirror.categories ?? mirror.Categories ?? []).map((c: any) => ({
          category: c.category ?? c.Category,
          categoryLabel: c.categoryLabel ?? c.CategoryLabel,
          lineCount: Number(c.lineCount ?? c.LineCount ?? 0),
          totalAmount: Number(c.totalAmount ?? c.TotalAmount ?? 0)
        }))
      },
      contracts: (raw.contracts ?? raw.Contracts ?? []).map((c: any) => ({
        b2BContractId: c.b2BContractId ?? c.B2BContractId,
        contractNumber: c.contractNumber ?? c.ContractNumber,
        contractName: c.contractName ?? c.ContractName,
        companyName: c.companyName ?? c.CompanyName,
        status: c.status ?? c.Status,
        totalAmount: Number(c.totalAmount ?? c.TotalAmount ?? 0),
        categories: (c.categories ?? c.Categories ?? []).map((cat: any) => ({
          category: cat.category ?? cat.Category,
          categoryLabel: cat.categoryLabel ?? cat.CategoryLabel,
          lineCount: Number(cat.lineCount ?? cat.LineCount ?? 0),
          totalAmount: Number(cat.totalAmount ?? cat.TotalAmount ?? 0),
          draftExists: !!(cat.draftExists ?? cat.DraftExists),
          existingDraftInvoiceId: cat.existingDraftInvoiceId ?? cat.ExistingDraftInvoiceId,
          lines: (cat.lines ?? cat.Lines ?? []).map((l: any) => ({
            b2BContractItemId: l.b2BContractItemId ?? l.B2BContractItemId,
            vehicleId: l.vehicleId ?? l.VehicleId,
            plate: l.plate ?? l.Plate,
            sourceType: l.sourceType ?? l.SourceType,
            sourceId: l.sourceId ?? l.SourceId,
            description: l.description ?? l.Description,
            reference: l.reference ?? l.Reference,
            periodStart: l.periodStart ?? l.PeriodStart,
            periodEnd: l.periodEnd ?? l.PeriodEnd,
            days: l.days ?? l.Days,
            unitAmount: l.unitAmount ?? l.UnitAmount,
            amount: Number(l.amount ?? l.Amount ?? 0)
          }))
        }))
      }))
    };
  }

  private normalizeInvoice(raw: any): B2BInvoice {
    return {
      id: raw.id ?? raw.Id,
      b2BContractId: raw.b2BContractId ?? raw.B2BContractId,
      contractNumber: raw.contractNumber ?? raw.ContractNumber,
      contractName: raw.contractName ?? raw.ContractName,
      companyName: raw.companyName ?? raw.CompanyName,
      customerId: raw.customerId ?? raw.CustomerId,
      competenceYear: raw.competenceYear ?? raw.CompetenceYear,
      competenceMonth: raw.competenceMonth ?? raw.CompetenceMonth,
      category: raw.category ?? raw.Category,
      categoryLabel: raw.categoryLabel ?? raw.CategoryLabel,
      status: raw.status ?? raw.Status,
      invoiceNumber: raw.invoiceNumber ?? raw.InvoiceNumber,
      totalAmount: Number(raw.totalAmount ?? raw.TotalAmount ?? 0),
      dueDate: raw.dueDate ?? raw.DueDate,
      issuedAt: raw.issuedAt ?? raw.IssuedAt,
      paidAt: raw.paidAt ?? raw.PaidAt,
      accountsReceivableId: raw.accountsReceivableId ?? raw.AccountsReceivableId,
      notes: raw.notes ?? raw.Notes,
      createdAt: raw.createdAt ?? raw.CreatedAt,
      items: (raw.items ?? raw.Items ?? []).map((i: any) => ({
        id: i.id ?? i.Id,
        b2BContractItemId: i.b2BContractItemId ?? i.B2BContractItemId,
        vehicleId: i.vehicleId ?? i.VehicleId,
        plate: i.plate ?? i.Plate,
        sourceType: i.sourceType ?? i.SourceType,
        sourceId: i.sourceId ?? i.SourceId,
        description: i.description ?? i.Description,
        reference: i.reference ?? i.Reference,
        periodStart: i.periodStart ?? i.PeriodStart,
        periodEnd: i.periodEnd ?? i.PeriodEnd,
        days: i.days ?? i.Days,
        unitAmount: i.unitAmount ?? i.UnitAmount,
        amount: Number(i.amount ?? i.Amount ?? 0)
      }))
    };
  }
}
